'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { PhoneOff, Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { useWebSocket } from '@/hooks/useWebSocket';

interface VideoCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  callType: 'audio' | 'video';
  contactName: string;
  contactId: string;
  chatId: string;
  isIncoming?: boolean;
  offer?: RTCSessionDescriptionInit;
}

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ],
};

export default function VideoCallModal({
  isOpen,
  onClose,
  callType,
  contactName,
  contactId,
  chatId,
  isIncoming = false,
  offer,
}: VideoCallModalProps) {
  const { subscribe, sendCallOffer, sendCallAnswer, sendIceCandidate, sendCallEnd } = useWebSocket();
  const [callState, setCallState] = useState<'ringing' | 'connecting' | 'connected' | 'ended'>('ringing');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(callType === 'audio');
  const [duration, setDuration] = useState(0);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationRef = useRef(0);
  const wasRejectedRef = useRef(false);
  const startedRef = useRef(false);

  const startDurationTimer = useCallback(() => {
    durationIntervalRef.current = setInterval(() => {
      setDuration((prev) => {
        durationRef.current = prev + 1;
        return prev + 1;
      });
    }, 1000);
  }, []);

  const endCall = useCallback(() => {
    setCallState('ended');
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    // Signal the other party — server auto-creates the call log
    sendCallEnd(contactId, chatId);
    setTimeout(onClose, 1500);
  }, [contactId, chatId, sendCallEnd, onClose]);

  const handleAnswer = useCallback((data: any) => {
    if (data.chatId !== chatId) return;
    if (pcRef.current && data.answer && pcRef.current.signalingState === 'have-local-offer') {
      pcRef.current.setRemoteDescription(new RTCSessionDescription(data.answer))
        .then(() => setCallState('connecting'))
        .catch((err) => console.warn('[Call] Failed to set remote answer:', err.message));
    }
  }, [chatId]);

  const handleIceCandidate = useCallback((data: any) => {
    if (data.chatId !== chatId) return;
    if (pcRef.current && data.candidate) {
      pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
    }
  }, [chatId]);

  const handleCallEnded = useCallback(() => {
    endCall();
  }, [endCall]);

  const handleCallRejected = useCallback(() => {
    wasRejectedRef.current = true;
    endCall();
  }, [endCall]);

  useEffect(() => {
    if (!isOpen) return;
    const unsubAnswer = subscribe('call_answer', handleAnswer);
    const unsubIce = subscribe('ice_candidate', handleIceCandidate);
    const unsubEnd = subscribe('call_ended', handleCallEnded);
    const unsubReject = subscribe('call_rejected', handleCallRejected);

    return () => {
      unsubAnswer();
      unsubIce();
      unsubEnd();
      unsubReject();
    };
  }, [isOpen, subscribe, handleAnswer, handleIceCandidate, handleCallEnded, handleCallRejected]);

  const startCall = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: callType === 'video',
      });
      localStreamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = new RTCPeerConnection(ICE_SERVERS);
      pcRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendIceCandidate(contactId, chatId, event.candidate);
        }
      };

      pc.ontrack = (event) => {
        if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
      };

      pc.oniceconnectionstatechange = () => {
        // 'connected' or 'completed' means media is flowing
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          setCallState('connected');
          startDurationTimer();
        }
      };

      pc.onconnectionstatechange = () => {
        // Only end on 'failed' — 'disconnected' is transient during ICE negotiation
        if (pc.connectionState === 'failed') {
          endCall();
        }
      };

      if (!isIncoming) {
        const sdpOffer = await pc.createOffer();
        await pc.setLocalDescription(sdpOffer);
        sendCallOffer(contactId, chatId, callType, sdpOffer);
        setCallState('ringing');
      } else if (offer) {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendCallAnswer(contactId, chatId, answer);
        setCallState('connecting');
      }
    } catch (err) {
      console.error('Failed to start call:', err);
      endCall();
    }
  }, [callType, contactId, chatId, isIncoming, offer, sendCallOffer, sendCallAnswer, sendIceCandidate, startDurationTimer, endCall]);

  useEffect(() => {
    if (isOpen && !startedRef.current) {
      startedRef.current = true;
      startCall();
    }
    return () => {
      if (!isOpen) {
        startedRef.current = false;
        if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
        if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => t.stop());
        if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
      }
    };
  }, [isOpen]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70]">
      <div className="bg-gray-900 rounded-2xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl">
        {/* Video area */}
        <div className="relative aspect-video bg-gray-800">
          <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />

          {callType === 'video' && (
            <div className="absolute bottom-4 right-4 w-32 h-24 rounded-lg overflow-hidden border-2 border-white/30 shadow-lg">
              <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
            </div>
          )}

          {callState !== 'connected' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/60">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-white">
                  {contactName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </span>
              </div>
              <p className="text-white font-medium text-lg">{contactName}</p>
              <p className="text-gray-300 text-sm mt-1">
                {callState === 'ringing' && (isIncoming ? 'Incoming call...' : 'Ringing...')}
                {callState === 'connecting' && 'Connecting...'}
                {callState === 'ended' && 'Call ended'}
              </p>
            </div>
          )}

          {callState === 'connected' && (
            <div className="absolute top-4 left-4 px-3 py-1 bg-black/50 rounded-full">
              <span className="text-white text-sm font-mono">{formatDuration(duration)}</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 py-5 bg-gray-900">
          <button
            onClick={toggleMute}
            className={`p-3.5 rounded-full transition-colors ${isMuted ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
          </button>

          {callType === 'video' && (
            <button
              onClick={toggleVideo}
              className={`p-3.5 rounded-full transition-colors ${isVideoOff ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
              title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
            >
              {isVideoOff ? <VideoOff size={20} /> : <Video size={20} />}
            </button>
          )}

          <button
            onClick={endCall}
            className="p-3.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
            title="End call"
          >
            <PhoneOff size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
